import React, { useEffect, useState } from "react";
import { api } from "../api";
import NovelExactPdfReviewPaneV1 from "./NovelExactPdfReviewPaneV1";
import NextScenesConfirmDialog from "./NextScenesConfirmDialog";

// Reuses the already approved interior's exact archive, artifact and page count.
// Never captures sources, regenerates a PDF, approves without review or publishes.
const SHA = /^[a-f0-9]{64}$/;
const ID = /^[a-f0-9]{24}$/i;
export const COVER_ACK = "APPROVE AND LOCK THIS EXACT COVER PDF";
export const matchingCoverStatus = (s, interiorSha) => Boolean(
  s?.ok === true && ID.test(String(s.coverArtifactId || "")) &&
  SHA.test(String(s.coverPdfSha256 || "")) &&
  s.exactInteriorPdfSha256 === interiorSha &&
  Number.isInteger(s.reviewRevision) && s.reviewRevision >= 1 &&
  Number.isInteger(s.physicalPageCount) && s.physicalPageCount >= 24 &&
  s.physicalPageCount % 2 === 0 &&
  ["prepared", "approved_locked"].includes(s.state) &&
  s.pairAvailable === (s.state === "approved_locked") &&
  s.publicationEnabled === false
);

export async function verifyCoverPilotPdf(blob, expectedSha, headerSha) {
  if (!blob || !/^application\/pdf(?:;|$)/i.test(blob.type || "") ||
      blob.size < 500 || blob.size > 8 * 1024 * 1024 ||
      !SHA.test(String(expectedSha || "")) || headerSha !== expectedSha) {
    throw new Error("Stored PDF identity is invalid.");
  }
  const cryptoSubtle = typeof window !== "undefined" ? window.crypto?.subtle : null;
  if (typeof cryptoSubtle?.digest !== "function") {
    throw new Error("Secure PDF verification requires a secure browser context.");
  }
  const hashed = await cryptoSubtle.digest("SHA-256", await blob.arrayBuffer());
  const actual = Array.from(new Uint8Array(hashed), b => b.toString(16).padStart(2, "0")).join("");
  if (actual !== expectedSha) throw new Error("Received PDF bytes differ from the approved file.");
  return true;
}

export default function NovelPaperbackCoverPilotB2cV1({ storyId, snapshotId, interiorArtifactId,
  sourceFingerprint, interiorPdfSha256, physicalPageCount, isFR = false }) {
  const label = (en, fr) => isFR ? fr : en;
  const [busy, setBusy] = useState("loading");
  const [error, setError] = useState("");
  const [loadBlocked, setLoadBlocked] = useState(false);
  const [status, setStatus] = useState(null);
  const [spineColour, setSpineColour] = useState("#333333");
  const [reviewUrl, setReviewUrl] = useState("");
  const [coverReviewedHash, setCoverReviewedHash] = useState("");
  const [ack, setAck] = useState(false);
  const [pendingCoverConfirmation, setPendingCoverConfirmation] = useState(null);
  const [files, setFiles] = useState(null);
  const [approvedView, setApprovedView] = useState("interior");
  useEffect(() => () => {
    if (reviewUrl) URL.revokeObjectURL(reviewUrl);
    if (files) {
      URL.revokeObjectURL(files.coverUrl);
      URL.revokeObjectURL(files.interiorUrl);
    }
  }, [reviewUrl, files]);

  useEffect(() => {
    let active = true;
    setStatus(null); setBusy("loading"); setError(""); setLoadBlocked(false);
    setReviewUrl(""); setCoverReviewedHash(""); setAck(false); setFiles(null);
    async function restore() {
      try {
        if (![storyId,snapshotId,interiorArtifactId].every(x => ID.test(String(x || ""))) ||
            !SHA.test(String(sourceFingerprint || "")) ||
            !SHA.test(String(interiorPdfSha256 || "")) ||
            !Number.isInteger(physicalPageCount) || physicalPageCount < 24 || physicalPageCount % 2) {
          throw new Error("Verified locked paperback interior required.");
        }
        let value;
        try { value = await api.getNovelPaperbackCoverStatus(storyId, snapshotId, interiorArtifactId); }
        catch(e) { if (e?.status === 409 && e?.data?.code === "novel_cover_not_prepared") value = null; else throw e; }
        if (value && (!matchingCoverStatus(value, interiorPdfSha256) ||
            value.physicalPageCount !== physicalPageCount)) {
          throw new Error("Saved cover review does not match the approved interior.");
        }
        if (active) setStatus(value);
      } catch(e) {
        if (active) { setLoadBlocked(true); setError(e?.status === 404
          ? "Paperback cover approval is not enabled for this book."
          : String(e?.message || "Cover review is unavailable.")); }
      } finally { if (active) setBusy(""); }
    }
    restore(); return () => { active = false; };
  }, [storyId, snapshotId, interiorArtifactId, sourceFingerprint, interiorPdfSha256, physicalPageCount]);

  const ids = [storyId, snapshotId, interiorArtifactId];
  async function refresh(expectedId, expectedRevision, expectedState) {
    const s = await api.getNovelPaperbackCoverStatus(...ids);
    if (!matchingCoverStatus(s, interiorPdfSha256) || s.physicalPageCount !== physicalPageCount ||
        (expectedId && s.coverArtifactId !== expectedId) ||
        (expectedRevision && s.reviewRevision !== expectedRevision) ||
        (expectedState && s.state !== expectedState)) {
      throw new Error("Stored cover state changed; reload and verify it before continuing.");
    }
    setStatus(s); return s;
  }
  function requestPrepare() {
    if (busy || loadBlocked || status?.state === "approved_locked") return;
    if (status) {
      setPendingCoverConfirmation({ kind: "replace", storyId, snapshotId,
        interiorArtifactId, interiorPdfSha256, coverArtifactId: status.coverArtifactId,
        reviewRevision: status.reviewRevision, coverPdfSha256: status.coverPdfSha256 });
      return;
    }
    void prepare();
  }
  async function prepare(confirmed = null) {
    if (busy || loadBlocked || status?.state === "approved_locked") return;
    if (status && (!confirmed || confirmed.kind !== "replace" ||
        confirmed.storyId !== storyId || confirmed.snapshotId !== snapshotId ||
        confirmed.interiorArtifactId !== interiorArtifactId ||
        confirmed.interiorPdfSha256 !== interiorPdfSha256 ||
        confirmed.coverArtifactId !== status.coverArtifactId ||
        confirmed.reviewRevision !== status.reviewRevision ||
        confirmed.coverPdfSha256 !== status.coverPdfSha256)) return;
    setBusy("prepare"); setError(""); setReviewUrl(""); setCoverReviewedHash(""); setAck(false); setFiles(null);
    try {
      const nextRevision = status?.reviewRevision || 0;
      const result = await api.prepareNovelPaperbackCover(...ids, {
        expectedFingerprint: sourceFingerprint, expectedInteriorPdfSha256: interiorPdfSha256,
        expectedLatestRevision: nextRevision, spineBackgroundHex: spineColour,
      });
      if (result?.ok !== true || !ID.test(result.coverArtifactId) ||
          result.reviewRevision !== nextRevision + 1 ||
          !SHA.test(result.coverPdfSha256) || result.exactInteriorPdfSha256 !== interiorPdfSha256) {
        throw new Error("Could not confirm exact prepared-cover identity.");
      }
      const s = await refresh(result.coverArtifactId, result.reviewRevision, "prepared");
      if (s.coverPdfSha256 !== result.coverPdfSha256) {
        throw new Error("Cover bytes differ from the saved review.");
      }
    } catch(e) { setError(String(e?.message || "Cover preparation failed.")); }
    finally { setBusy(""); }
  }
  async function review() {
    if (busy || !status || status.state !== "prepared") return;
    setBusy("review"); setError(""); setReviewUrl(""); setCoverReviewedHash(""); setAck(false);
    try {
      const fresh = await refresh(status.coverArtifactId, status.reviewRevision, "prepared");
      const pdf = await api.getNovelPaperbackCoverPdf(...ids, fresh.coverArtifactId, "review");
      if (pdf.matchingSha256 !== interiorPdfSha256 || pdf.printStatus !== "COVER-REVIEW-NOT-APPROVED")
        throw new Error("Cover review is not bound to this locked interior.");
      await verifyCoverPilotPdf(pdf.blob, fresh.coverPdfSha256, pdf.sha256);
      setReviewUrl(URL.createObjectURL(pdf.blob));
      setCoverReviewedHash(fresh.coverPdfSha256);
    } catch(e) { setError(String(e?.message || "Could not verify the exact cover PDF.")); }
    finally { setBusy(""); }
  }
  function requestCoverApproval() {
    if (busy || !status || status.state !== "prepared" || !reviewUrl || !ack ||
        coverReviewedHash !== status.coverPdfSha256) return;
    setPendingCoverConfirmation({ kind: "approve", storyId, snapshotId,
      interiorArtifactId, interiorPdfSha256, coverArtifactId: status.coverArtifactId,
      reviewRevision: status.reviewRevision, coverPdfSha256: status.coverPdfSha256 });
  }
  async function approve(confirmed) {
    if (busy || !status || status.state !== "prepared" || !reviewUrl || !ack ||
        coverReviewedHash !== status.coverPdfSha256 || !confirmed ||
        confirmed.kind !== "approve" || confirmed.storyId !== storyId ||
        confirmed.snapshotId !== snapshotId ||
        confirmed.interiorArtifactId !== interiorArtifactId ||
        confirmed.interiorPdfSha256 !== interiorPdfSha256 ||
        confirmed.coverArtifactId !== status.coverArtifactId ||
        confirmed.reviewRevision !== status.reviewRevision ||
        confirmed.coverPdfSha256 !== status.coverPdfSha256) return;
    setBusy("approve"); setError("");
    try {
      const result = await api.approveNovelPaperbackCover(...ids, status.coverArtifactId, {
        expectedRevision: status.reviewRevision, expectedCoverPdfSha256: status.coverPdfSha256,
        expectedInteriorPdfSha256: interiorPdfSha256, acknowledgment: COVER_ACK,
      });
      if (result?.ok !== true || result.state !== "approved_locked" ||
          result.coverArtifactId !== status.coverArtifactId ||
          result.coverPdfSha256 !== status.coverPdfSha256 ||
          result.exactInteriorPdfSha256 !== interiorPdfSha256) {
        throw new Error("Cover approval result could not be verified.");
      }
      await refresh(status.coverArtifactId, status.reviewRevision, "approved_locked");
      setReviewUrl(""); setCoverReviewedHash(""); setAck(false);
    } catch(e) { setError(String(e?.message || "Approval not confirmed. Reload before retrying.")); }
    finally { setBusy(""); }
  }
  async function loadPair() {
    if (busy || !status || status.state !== "approved_locked" || !status.pairAvailable) return;
    setBusy("download"); setFiles(null); setError("");
    try {
      const fresh = await refresh(status.coverArtifactId, status.reviewRevision, "approved_locked");
      const [cover, interior] = await Promise.all([
        api.getNovelPaperbackCoverPdf(...ids, "", "final-cover"),
        api.getNovelPaperbackCoverPdf(...ids, "", "final-interior"),
      ]);
      if (cover.matchingSha256 !== interiorPdfSha256 ||
          interior.matchingSha256 !== fresh.coverPdfSha256 ||
          cover.printStatus !== "APPROVED-COVER-NOT-PUBLISHED" ||
          interior.printStatus !== "APPROVED-INTERIOR-NOT-PUBLISHED") {
        throw new Error("The cover and interior are not the same approved pair.");
      }
      await Promise.all([
        verifyCoverPilotPdf(cover.blob, fresh.coverPdfSha256, cover.sha256),
        verifyCoverPilotPdf(interior.blob, interiorPdfSha256, interior.sha256),
      ]);
      setFiles({coverUrl: URL.createObjectURL(cover.blob),
        interiorUrl: URL.createObjectURL(interior.blob)});
      setApprovedView("interior");
    } catch(e) { setError(String(e?.message || "Approved files could not be verified.")); }
    finally { setBusy(""); }
  }
  return (
    <section data-novel-b2c-cover-pilot="owner-only" style={{ display:"grid", gap:12,
      borderTop:"1px solid #9acbb7", paddingTop:16 }}>
      <h4 style={{margin:0}}>{label("Next: Paperback Cover and Two Approved PDFs",
        "Ensuite : couverture brochée et deux PDF approuvés")}</h4>
      <p className="ns-card-meta" style={{margin:0}}>{label(
        "The approved interior stays locked. Review the exact full-wrap cover before approving it. No KDP submission occurs here.",
        "L’intérieur approuvé reste verrouillé. Vérifiez la couverture complète avant son approbation. Aucune soumission KDP ici.")}</p>
      {busy === "loading" ? <p role="status">{label("Checking saved cover…","Vérification…")}</p> : null}
      {error ? <p role="alert" style={{color:"#9a3412"}}>{error}</p> : null}
      {status ? <p data-novel-b2c-review-state={status.state} className="ns-card-meta">
        {label("Cover revision","Version couverture")}: {status.reviewRevision}
        {" · "}{status.state === "approved_locked" ? label("Approved and locked","Approuvé et verrouillé") :
          label("Awaiting exact review","En attente de vérification")}
      </p> : null}
      {!busy && !loadBlocked && status?.state !== "approved_locked" ? <>
        <label>{label("Spine background","Couleur du dos")} {" "}
          <input aria-label="Spine background" type="color" value={spineColour}
            disabled={Boolean(busy)} onChange={e => setSpineColour(e.target.value)} /></label>
        <button className="ns-button ns-button-outline" type="button" disabled={Boolean(busy)}
          data-novel-b2c-prepare="true" onClick={requestPrepare}>
          {status ? label("Prepare Revised Cover PDF", "Préparer une nouvelle version") :
            label("Prepare Exact Cover PDF", "Préparer le PDF couverture")}</button>
        {status?.state === "prepared" ? <button className="ns-button ns-button-outline" type="button"
          data-novel-b2c-review="true" onClick={review} disabled={Boolean(busy)}>
          {label("Open Exact Cover Review", "Ouvrir la vérification de la couverture")}</button> : null}
        {reviewUrl ? <>
          <iframe title="Exact paperback cover review PDF" src={reviewUrl}
            style={{width:"100%",height:"min(70vh, 760px)",minHeight:360,border:"1px solid #bdd4c6"}} />
          <label style={{display:"flex",gap:10,alignItems:"flex-start"}}>
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />
            {label("I inspected this exact full-wrap cover and approve locking it to the interior PDF above.",
              "J’ai vérifié cette couverture exacte et j’approuve son verrouillage à l’intérieur ci-dessus.")}</label>
          <button type="button" className="ns-button ns-button-primary" onClick={requestCoverApproval}
            data-novel-b2c-approve="true" disabled={Boolean(busy) || !ack ||
              coverReviewedHash !== status?.coverPdfSha256}>
            {label("Approve and Lock Exact Cover", "Approuver la couverture exacte")}</button>
        </> : null}
      </> : null}
      {status?.state === "approved_locked" ? <>
        <button type="button" className="ns-button ns-button-primary" disabled={Boolean(busy)}
          data-novel-b2c-load-pair="true" onClick={loadPair}>
          {label("Verify Both Approved PDFs", "Vérifier les deux PDF approuvés")}</button>
        {files ? <>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            <a className="ns-button ns-button-outline" href={files.interiorUrl}
              data-novel-b2c-download-interior="true" download="NextScenes_Approved_Paperback_Interior.pdf">
              {label("Download Approved Interior PDF", "Télécharger l’intérieur approuvé")}</a>
            <a className="ns-button ns-button-outline" href={files.coverUrl}
              data-novel-b2c-download-cover="true" download="NextScenes_Approved_Paperback_Cover.pdf">
              {label("Download Approved Cover PDF", "Télécharger la couverture approuvée")}</a>
          </div>
          <section data-novel-b2c-approved-pair-viewer="read-only"
            style={{display:"grid",gap:12,minWidth:0}}>
            <strong>{label("View Your Approved Book — Read Only",
              "Voir votre livre approuvé — lecture seule")}</strong>
            <p className="ns-card-meta" style={{margin:0}}>
              {label("Both original PDFs have been verified against the saved approval. Viewing does not unlock, revise or approve them again.",
                "Les deux PDF originaux ont été vérifiés par rapport à l’approbation enregistrée. La consultation ne les déverrouille pas, ne les modifie pas et ne les approuve pas de nouveau.")}
            </p>
            <div role="group" aria-label={label("Approved book file to inspect", "Fichier approuvé à consulter")}
              style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              <button type="button" className={approvedView==="interior"
                  ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
                aria-pressed={approvedView==="interior"}
                data-novel-b2c-view-interior="true" onClick={()=>setApprovedView("interior")}>
                {label("View Approved Interior", "Voir l’intérieur approuvé")}
              </button>
              <button type="button" className={approvedView==="cover"
                  ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
                aria-pressed={approvedView==="cover"}
                data-novel-b2c-view-cover="true" onClick={()=>setApprovedView("cover")}>
                {label("View Approved Cover", "Voir la couverture approuvée")}
              </button>
            </div>
            <NovelExactPdfReviewPaneV1
              pdfUrl={approvedView==="cover" ? files.coverUrl : files.interiorUrl}
              kind={approvedView} isFR={isFR} approved />
          </section>
        </> : null}
      </> : null}
      <p className="ns-card-meta" style={{margin:0}}>{label(
        "These are author-downloadable files for manual Amazon KDP upload and Print Previewer validation; NextScenes has not published this book.",
        "Ces fichiers sont destinés à un dépôt manuel sur Amazon KDP et vérification dans Print Previewer ; NextScenes n’a pas publié ce livre.")}</p>
      <NextScenesConfirmDialog
        open={Boolean(pendingCoverConfirmation)}
        title={pendingCoverConfirmation?.kind === "replace"
          ? label("Replace unapproved cover review?", "Remplacer la couverture non approuvée ?")
          : label("Lock this exact cover PDF?", "Verrouiller ce PDF de couverture exact ?")}
        message={pendingCoverConfirmation?.kind === "replace"
          ? label("This replaces only the current unapproved cover review with a new revision. An already approved interior remains unchanged. Cancel to keep the current review.",
            "Cette action remplace uniquement l'examen de couverture non approuvé. L'intérieur approuvé reste inchangé. Annulez pour conserver la version actuelle.")
          : label("Approve and permanently lock this exact reviewed cover against the approved interior? This decision cannot be undone.",
            "Approuver et verrouiller définitivement cette couverture exacte avec l'intérieur approuvé ? Cette décision est irréversible.")}
        stayLabel={label("Cancel", "Annuler")}
        leaveLabel={pendingCoverConfirmation?.kind === "replace"
          ? label("Prepare Revised Cover", "Préparer la couverture révisée")
          : label("Approve and Lock Cover", "Approuver et verrouiller la couverture")}
        onStay={() => setPendingCoverConfirmation(null)}
        onLeave={() => {
          const confirmed = pendingCoverConfirmation;
          setPendingCoverConfirmation(null);
          if (confirmed?.kind === "replace") void prepare(confirmed);
          else if (confirmed?.kind === "approve") void approve(confirmed);
        }}
      />
    </section>
  );
}
